import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Printer, Users, DollarSign, Baby,
  Loader2, Phone, Mail, MapPin, User, Key, Copy, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import api from '../../api';

const TABS = ['Overview', 'Children', 'Fees'];

export default function ParentDetailPage() {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('Overview');

  useEffect(() => {
    setLoading(true);
    api.get(`/school/parents/${parentId}/summary`)
      .then(d => { if (d.success) setSummary(d.data); })
      .catch(err => toast.error(err?.detail || 'Failed to load parent'))
      .finally(() => setLoading(false));
  }, [parentId]);

  const handlePrint = () => {
    if (!summary) return;
    const { parent, children, fee_totals } = summary;
    const fullName = `${parent.first_name} ${parent.last_name}`;
    const pw = window.open('', '_blank', 'width=800,height=1000');
    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Parent Profile – ${fullName}</title>
  <style>
    * { margin:0; padding:0; box-sizing:border-box; }
    body { font-family:'Segoe UI',Arial,sans-serif; background:#fff; color:#1e293b; font-size:13px; }
    .card { max-width:680px; margin:20px auto; border:1.5px solid #e2e8f0; border-radius:12px; overflow:hidden; }
    .header { background:linear-gradient(135deg,#7c3aed 0%,#a855f7 100%); color:white; padding:24px 28px; display:flex; align-items:center; gap:20px; }
    .avatar { width:72px; height:72px; border-radius:12px; border:3px solid rgba(255,255,255,.6); background:#a78bfa; display:flex; align-items:center; justify-content:center; font-size:24px; font-weight:800; color:white; flex-shrink:0; }
    .header-text h1 { font-size:22px; font-weight:700; margin-bottom:4px; }
    .header-text p { opacity:.8; font-size:13px; }
    .kpis { display:grid; grid-template-columns:repeat(3,1fr); gap:1px; background:#e2e8f0; }
    .kpi { background:#f8fafc; padding:12px 16px; text-align:center; }
    .kpi-val { font-size:18px; font-weight:700; color:#1e293b; }
    .kpi-label { font-size:10px; color:#64748b; text-transform:uppercase; letter-spacing:.5px; margin-top:2px; }
    .section { padding:20px 28px; border-bottom:1px solid #f1f5f9; }
    .section h2 { font-size:10px; text-transform:uppercase; letter-spacing:1px; color:#94a3b8; font-weight:700; margin-bottom:12px; }
    .field { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f1f5f9; }
    .field-label { color:#64748b; font-size:11px; font-weight:600; }
    .field-value { font-weight:600; color:#1e293b; font-size:12px; }
    .footer { padding:10px 28px; text-align:center; font-size:10px; color:#94a3b8; background:#f8fafc; }
    @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } }
  </style>
</head>
<body>
  <div class="card">
    <div class="header">
      <div class="avatar">${(parent.first_name?.[0]||'')+(parent.last_name?.[0]||'')}</div>
      <div class="header-text">
        <h1>${fullName}</h1>
        <p>Guardian · ${children.length} child${children.length !== 1 ? 'ren' : ''} enrolled</p>
        <p>${parent.email}</p>
      </div>
    </div>
    <div class="kpis">
      <div class="kpi"><div class="kpi-val">${children.length}</div><div class="kpi-label">Children</div></div>
      <div class="kpi"><div class="kpi-val">₦${fee_totals.total.toLocaleString()}</div><div class="kpi-label">Total Fees</div></div>
      <div class="kpi"><div class="kpi-val">₦${fee_totals.outstanding.toLocaleString()}</div><div class="kpi-label">Outstanding</div></div>
    </div>
    <div class="section">
      <h2>Contact Information</h2>
      ${[['Email',parent.email||'—'],['Phone',parent.phone||'—'],['Address',parent.address||'—']].map(([l,v])=>`<div class="field"><span class="field-label">${l}</span><span class="field-value">${v}</span></div>`).join('')}
    </div>
    <div class="section">
      <h2>Enrolled Children</h2>
      ${children.map(c=>`<div class="field"><span class="field-label">${c.first_name} ${c.last_name}</span><span class="field-value">${c.class_name||'—'} · ${c.admission_number}</span></div>`).join('')||'<p style="color:#94a3b8;font-size:12px;">No children linked</p>'}
    </div>
    <div class="footer">Printed on ${new Date().toLocaleDateString('en-NG',{weekday:'long',year:'numeric',month:'long',day:'numeric'})} · EDUVA School Management System</div>
  </div>
  <script>window.onload=function(){window.print();window.onafterprint=function(){window.close();};};</script>
</body>
</html>`;
    pw.document.write(html);
    pw.document.close();
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Loader2 className="w-8 h-8 text-violet-600 animate-spin" />
    </div>
  );

  if (!summary) return (
    <div className="p-8 text-center text-slate-500">Parent not found</div>
  );

  const { parent, children, fee_totals } = summary;
  const fullName = `${parent.first_name} ${parent.last_name}`;
  const initials = `${parent.first_name?.[0] || ''}${parent.last_name?.[0] || ''}`;

  return (
    <div className="p-4 lg:p-6 max-w-5xl mx-auto" data-testid="parent-detail-page">
      {/* Back + actions */}
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate('/school/parents')}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 text-sm font-medium transition-colors"
          data-testid="back-to-parents">
          <ArrowLeft className="w-4 h-4" /> Back to Parents
        </button>
        <button onClick={handlePrint}
          className="flex items-center gap-1.5 bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium px-3 py-2 rounded-xl transition-colors"
          data-testid="print-parent-btn">
          <Printer className="w-3.5 h-3.5" /> Print Profile
        </button>
      </div>

      {/* Hero card */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm mb-5 overflow-hidden">
        <div className="h-24 bg-gradient-to-r from-violet-600 to-purple-600" />
        <div className="px-6 pb-5">
          <div className="flex items-end gap-4 -mt-12 mb-4">
            <div className="w-20 h-20 rounded-2xl border-4 border-white shadow-md bg-violet-100 flex items-center justify-center flex-shrink-0">
              <span className="text-2xl font-extrabold text-violet-600">{initials}</span>
            </div>
            <div className="pb-1 flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900 truncate">{fullName}</h1>
                <Badge className="text-xs bg-violet-100 text-violet-700">Guardian</Badge>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{parent.email} {parent.phone && `· ${parent.phone}`}</p>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-3 gap-3">
            <Kpi icon={Baby} label="Children" value={children.length} sub="Enrolled" color="emerald" />
            <Kpi icon={DollarSign} label="Total Fees" value={`₦${(fee_totals.total || 0).toLocaleString()}`} sub="All invoices" color="blue" />
            <Kpi icon={DollarSign} label="Outstanding" value={`₦${(fee_totals.outstanding || 0).toLocaleString()}`} sub="Unpaid" color={fee_totals.outstanding > 0 ? 'red' : 'emerald'} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-100 px-4">
          {TABS.map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t ? 'border-violet-500 text-violet-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`parent-tab-${t.toLowerCase()}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="p-5">
          {tab === 'Overview' && <OverviewTab parent={parent} />}
          {tab === 'Children' && <ChildrenTab children={children} />}
          {tab === 'Fees' && <FeesTab children={children} feeTotals={fee_totals} />}
        </div>
      </div>
    </div>
  );
}

/* ─── Tab: Overview ─────────────────────────────────────────────── */
function OverviewTab({ parent }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Personal Information</h4>
        <div className="space-y-0 border border-slate-100 rounded-xl overflow-hidden">
          {[
            ['First Name', parent.first_name],
            ['Last Name', parent.last_name],
            ['Gender', parent.gender || '—'],
            ['Status', parent.is_active ? 'Active' : 'Inactive'],
          ].map(([l, v], i) => (
            <div key={l} className={`flex items-center justify-between px-4 py-2.5 ${i % 2 === 0 ? 'bg-slate-50/50' : 'bg-white'}`}>
              <span className="text-xs font-semibold text-slate-400">{l}</span>
              <span className="text-sm font-semibold text-slate-700">{v}</span>
            </div>
          ))}
        </div>

        {/* Login Credentials */}
        <div className="mt-3 bg-violet-50 border border-violet-100 rounded-xl p-3.5">
          <div className="flex items-center gap-2 mb-2">
            <Key className="w-3.5 h-3.5 text-violet-600" />
            <p className="text-xs font-bold text-violet-700 uppercase tracking-wide">Login Credentials</p>
          </div>
          <CredRow label="Email (Username)" value={parent.email} color="violet" />
          <CredRow label="Default Password" value={parent.phone || '—'} color="violet" />
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-3">Contact</h4>
        <div className="space-y-3">
          {[
            { icon: Mail, label: 'Email', value: parent.email },
            { icon: Phone, label: 'Phone', value: parent.phone || '—' },
            { icon: MapPin, label: 'Address', value: parent.address || '—' },
          ].map(g => (
            <div key={g.label} className="flex items-start gap-3 bg-slate-50 rounded-xl p-3.5 border border-slate-100">
              <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <g.icon className="w-4 h-4 text-violet-600" />
              </div>
              <div>
                <p className="text-xs text-slate-400 font-semibold">{g.label}</p>
                <p className="text-sm font-bold text-slate-700 mt-0.5">{g.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── Tab: Children ───────────────────────────────────────────────── */
function ChildrenTab({ children }) {
  const navigate = useNavigate();
  if (!children.length) return (
    <div className="text-center py-10 text-slate-400">
      <Baby className="w-8 h-8 mx-auto mb-2 opacity-40" />
      <p className="text-sm">No children linked to this parent</p>
    </div>
  );

  return (
    <div className="overflow-hidden rounded-xl border border-slate-100">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-100">
          <tr>
            <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Student</th>
            <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase hidden sm:table-cell">Class</th>
            <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase hidden md:table-cell">Admission No.</th>
            <th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Fees Due</th>
          </tr>
        </thead>
        <tbody>
          {children.map((c, i) => (
            <tr key={c.id} onClick={() => navigate(`/school/students/${c.id}`)}
              className={`border-b border-slate-50 cursor-pointer hover:bg-violet-50/30 group ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2">
                  {c.avatar
                    ? <img src={c.avatar} alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-violet-100" />
                    : <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center text-xs font-bold text-violet-600">{c.first_name?.[0]}{c.last_name?.[0]}</div>
                  }
                  <span className="font-semibold text-slate-800 group-hover:text-violet-700 transition-colors">{c.first_name} {c.last_name}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-slate-500 hidden sm:table-cell">{c.class_name || '—'}</td>
              <td className="px-4 py-3 text-slate-500 font-mono hidden md:table-cell text-xs">{c.admission_number}</td>
              <td className="px-4 py-3 text-right">
                {c.fee_outstanding > 0
                  ? <span className="text-xs font-bold text-red-600">₦{c.fee_outstanding.toLocaleString()}</span>
                  : <span className="text-xs font-bold text-emerald-600">Clear</span>
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Tab: Fees ───────────────────────────────────────────────────── */
function FeesTab({ children, feeTotals }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total Billed', value: feeTotals.total, color: 'slate' },
          { label: 'Total Paid', value: feeTotals.paid, color: 'emerald' },
          { label: 'Outstanding', value: feeTotals.outstanding, color: feeTotals.outstanding > 0 ? 'red' : 'emerald' },
        ].map(f => (
          <div key={f.label} className={`rounded-xl border p-3 ${f.color === 'emerald' ? 'bg-emerald-50 border-emerald-100' : f.color === 'red' ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`}>
            <p className={`text-lg font-extrabold ${f.color === 'emerald' ? 'text-emerald-700' : f.color === 'red' ? 'text-red-700' : 'text-slate-700'}`}>₦{f.value.toLocaleString()}</p>
            <p className="text-xs text-slate-500 font-semibold mt-0.5">{f.label}</p>
          </div>
        ))}
      </div>
      {children.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-slate-100">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Child</th>
                <th className="text-right px-4 py-2.5 text-xs font-bold text-slate-500 uppercase">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {children.map((c, i) => (
                <tr key={c.id} className={`border-b border-slate-50 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}>
                  <td className="px-4 py-2.5 font-semibold text-slate-800">{c.first_name} {c.last_name} <span className="text-slate-400 font-normal text-xs">· {c.class_name}</span></td>
                  <td className="px-4 py-2.5 text-right">
                    {c.fee_outstanding > 0
                      ? <span className="text-xs font-bold text-red-600">₦{c.fee_outstanding.toLocaleString()}</span>
                      : <span className="text-xs font-bold text-emerald-600">Clear</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Helpers ─────────────────────────────────────────────────────── */
function CredRow({ label, value, color = 'indigo' }) {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    if (!value || value === '—') return;
    navigator.clipboard.writeText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  };
  const c = color === 'violet' ? 'text-violet-500 text-violet-700' : 'text-indigo-500 text-indigo-700';
  return (
    <div className="flex items-center justify-between py-1">
      <span className={`text-xs font-semibold text-${color}-500`}>{label}</span>
      <div className="flex items-center gap-2">
        <span className={`text-sm font-bold text-${color}-700 font-mono`}>{value}</span>
        <button onClick={copy} className={`text-${color}-300 hover:text-${color}-600 transition-colors`} title="Copy">
          {copied ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, sub, color }) {
  const p = { indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700', emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700', violet: 'bg-violet-50 border-violet-100 text-violet-700', blue: 'bg-blue-50 border-blue-100 text-blue-700', red: 'bg-red-50 border-red-100 text-red-700' };
  return (
    <div className={`rounded-xl border p-3 ${p[color] || p.violet}`}>
      <Icon className="w-4 h-4 mb-1.5 opacity-70" />
      <p className="text-lg font-extrabold leading-tight">{value}</p>
      {sub && <p className="text-xs opacity-60 leading-tight">{sub}</p>}
      <p className="text-xs font-semibold opacity-50 mt-0.5 uppercase tracking-wider">{label}</p>
    </div>
  );
}
